// `avatar notify` — agent (Claude trong AvatarCodi) chủ động bắn OS notification
// khi user có thể đã rời màn hình (D1, plan os-notify-agent-proactive: chỉ
// đường agent tự quyết, không heartbeat hook).
//
// Fail-open TUYỆT ĐỐI: KHÔNG BAO GIỜ exit ≠0, không throw ra ngoài — lệnh này
// chạy giữa luồng agent, một lỗi ở đây không được phép làm gián đoạn task đang
// làm. Ngoài AvatarCodi / helper thiếu ⇒ no-op im lặng (stderr chỉ in khi có
// `--verbose`, dùng để debug tay).

import { spawn } from "node:child_process";
import { existsSync } from "node:fs";
import type { Command } from "commander";
import { resolveNotifyInvocation } from "../lib/build-os-notification-invocation.js";
import { detectHostPlatform, type HostPlatform } from "../lib/detect-host-platform.js";

export interface NotifyCommandOptions {
  title: string;
  body: string;
  subtitle?: string;
  sound?: boolean;
  verbose?: boolean;
}

export interface RunNotifyDeps {
  platform: HostPlatform;
  env: NodeJS.ProcessEnv;
  fileExists: (path: string) => boolean;
  spawnDetached: (command: string, args: string[]) => void;
  stderr: (s: string) => void;
}

/** Lõi test được — không exec thật, `spawnDetached` do caller inject. Luôn trả 0. */
export function runNotify(opts: NotifyCommandOptions, deps: RunNotifyDeps): number {
  // Bọc TOÀN BỘ thân hàm, không chỉ bước spawn: exception đồng bộ lọt khỏi
  // `.action()` bị top-level catch của src/index.ts biến thành exit 1 — phá lời
  // hứa fail-open ở đầu file (code-review phase-02 chỉ ra).
  try {
    const invocation = resolveNotifyInvocation({
      platform: deps.platform,
      env: deps.env,
      opts: { title: opts.title, body: opts.body, subtitle: opts.subtitle, sound: opts.sound },
      fileExists: deps.fileExists,
    });

    if (!invocation) {
      if (opts.verbose) {
        deps.stderr(
          "avatar notify: no-op (không phải host AvatarCodi, hoặc helper không tìm thấy)\n",
        );
      }
      return 0;
    }
    deps.spawnDetached(invocation.command, invocation.args);
  } catch (err) {
    if (opts.verbose) {
      const msg = err instanceof Error ? err.message : String(err);
      deps.stderr(`avatar notify: lỗi — ${msg}\n`);
    }
  }
  return 0;
}

export function registerNotifyCommand(program: Command): void {
  program
    .command("notify")
    .description("Bắn OS notification qua helper AvatarCodi (fail-open, no-op ngoài AvatarCodi)")
    .requiredOption("--body <text>", "Nội dung notification, tối đa 200 ký tự (bắt buộc)")
    .option("--title <text>", "Tiêu đề notification", "AvatarCodi")
    .option("--subtitle <text>", "Subtitle (chỉ có tác dụng trên macOS)")
    .option("--sound", "Phát âm thanh kèm notification")
    .option("--verbose", "In lý do no-op ra stderr khi không gửi được")
    .action((opts: NotifyCommandOptions) => {
      const code = runNotify(opts, {
        platform: detectHostPlatform(),
        env: process.env,
        fileExists: existsSync,
        spawnDetached: (command, args) => {
          const child = spawn(command, args, { detached: true, stdio: "ignore" });
          child.unref();
        },
        stderr: (s) => process.stderr.write(s),
      });
      process.exit(code);
    });
}
